import DetailsView from '../../Pandora/model/details-view';
import WizardView from '../../Pandora/model/wizard/wizard-view';
import { ContentWidgetDialogAction, ContentWidgetType } from '../model/enumerations';
import { TrackOptions } from '../../../Telemetry/telemetry.contracts';
import { SchemaService } from './schema-service';
import ContentWidgetService from './content-widget-service';
import { EventCodes } from '../../../App/model/event-codes';
import { EventService } from '../../../App/services/event-service';

export interface TrackViewTelemetryProps {
	eventName: string;
	view: DetailsView | WizardView;
	propsModifier?: (props: TrackOptions) => TrackOptions;
}

export class AgentTelemetryService {
	static $inject = ['mx.SolutionBuilderAgent.ContentWidgetService', 'mx.SolutionBuilderAgent.SchemaService', 'mx.shell.EventService'];

	constructor(
		private readonly contentWidgetService: ReturnType<typeof ContentWidgetService>,
		private readonly schemaService: SchemaService,
		private readonly eventService: EventService
	) {}

	public init() {
		this.eventService.subscribeGlobal(EventCodes.TrackViewTelemetry, (e, { eventName, view, propsModifier }: TrackViewTelemetryProps) => {
			this.trackViewTelemetry(eventName, view, propsModifier);
		});
	}

	private async trackViewTelemetry(eventName: string, view: DetailsView | WizardView, propsModifier?: TrackViewTelemetryProps['propsModifier']) {
		try {
			if (!view) {
				return;
			}
			let trackProps = await this.getTelemetryInfoFromView(eventName, view);
			if (propsModifier) {
				trackProps = propsModifier(trackProps);
			}
			this.eventService.dispatchEvent(EventCodes.TrackTelemetry, { ...trackProps });
		} catch (e) {
			//do nothing
		}
	}

	private async getTelemetryInfoFromView(eventName: string, view: DetailsView | WizardView) {
		const widgetType = this.contentWidgetService.getWidgetType(view);
		const widgetId = this.contentWidgetService.getWidgetIdFromView(view);

		let viewName = 'View';

		const trackProps: TrackOptions = {
			eventName,
			sender: widgetId,
			senderTitle: widgetId,
			mx42_uux_view_source: view.telemetrySource || 'Other',
		};

		if (widgetType === ContentWidgetType.ObjectDetails || widgetType === ContentWidgetType.Dialog) {
			const dialogView = view as DetailsView;
			viewName =
				dialogView.dialogType === ContentWidgetDialogAction.New ? 'Create dialog' : dialogView.dialogType === ContentWidgetDialogAction.Edit ? 'Edit dialog' : 'Preview';

			const typeInfo = await this.schemaService.getType(dialogView.context._type as string);

			if (dialogView.dialogType === ContentWidgetDialogAction.New) {
				trackProps.contextId = null;
				trackProps.contextTitle = null;
			} else {
				trackProps.contextId = dialogView.context._id ? ([dialogView.context._id] as string[]) : null;
				const contextTitle = (dialogView.context?._displayName || dialogView.context?._name) as string | undefined;
				trackProps.contextTitle = contextTitle ? [contextTitle] : null;
			}

			trackProps.contextTypeId = typeInfo?.Id ? [typeInfo?.Id] : null;
			trackProps.contextTypeTitle = typeInfo?.InternalName ? [typeInfo?.InternalName] : null;
			if (dialogView.widgetName) {
				trackProps.senderTitle = dialogView.widgetName;
			}
		} else if (widgetType === ContentWidgetType.Wizard) {
			const wizardView = view as WizardView;
			viewName = 'Wizard';
			const targets = wizardView.actionContext;

			const typesInfo = await Promise.all(
				targets
					.map((target) => target._type)
					.filter((value, index, array) => array.indexOf(value) === index)
					.map((type) => this.schemaService.getType(type))
			);
			trackProps.contextId = targets.map((target) => target._id);
			trackProps.contextTitle = targets.map((target) => target._displayName || (target._name as string));

			trackProps.contextTypeId = typesInfo.map((typeInfo) => typeInfo?.Id as string);
			trackProps.contextTypeTitle = typesInfo.map((typeInfo) => typeInfo?.InternalName as string);

			if (wizardView.name) {
				trackProps.senderTitle = wizardView.name;
			}
		}

		trackProps.eventName = trackProps.eventName.replace('{viewName}', viewName);

		return trackProps;
	}
}
